// React & Hooks
import { useContext, useRef } from 'react';

// Leaflet Core & Plugins
import 'leaflet.markercluster';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-path-drag';
import 'leaflet-path-transform';

// Custom Leaflet Extensions
import '@dist/Leaflet.ImageOverlay.Rotated';

// Store
import { useMapStore } from '../MapStore/UseMapStore';

// Context
import { ThemeContext } from '@context/ThemeContext';

// Hooks
import {
  useInitializeMap,
  useGatewaysLayer,
  usePlacesLayer,
  useAssetsLayer,
  useStationaryLayer,
  useMapManager,
  useMapAlerts,
  useRotatedOverlay,
} from '../MapHooks';

// Components
import { MapTourStartMenu } from '../../../../../components/layout/SonarMapTour/MapTourStartMenu';
import { MapEditMenu } from './MapEditMenu/MapEditMenu';
import { MapInfoMenu } from './MapInfoMenu/MapInfoMenu';
import { MapControls } from './MapMiscComponents/MapControls';

// Helper Functions
import { createAzureLayer, startUpgrade } from '../MapFunctions/MapHelperFunctions';

// Styles
import '../MapStyles/MapObject.scss';
import '@scss/variables.scss';

export function MapObject() {
  // Map References
  const azureTileRef = useRef(null);

  // Theme
  const { theme } = useContext(ThemeContext);

  // Map controls states
  const edit = useMapStore((state) => state.edit);
  const isStatic = useMapStore((state) => state.isStatic);
  const setSelected = useMapStore((state) => state.setSelected);
  const upgradeInProg = useMapStore((state) => state.upgradeInProg);
  const setUpgradeInProg = useMapStore((state) => state.setUpgradeInProg);
  const setOverlayFC = useMapStore((state) => state.setOverlayFC);

  // Map Feature Collections
  const overlayFC = useMapStore((state) => state.overlayFC);

  // Map Layers
  const mapInstance = useMapStore((state) => state.mapInstance);
  const setAzureLayer = useMapStore((state) => state.setAzureLayer);
  const azureLayer = useMapStore((state) => state.azureLayer);
  const imageLayer = useMapStore((state) => state.imageLayer);
  const boundsLayer = useMapStore((state) => state.boundsLayer);

  const mapGeoJSON = overlayFC.features[0];

  useInitializeMap();
  useMapAlerts();
  useMapManager();
  usePlacesLayer();
  useGatewaysLayer();
  useAssetsLayer();
  useStationaryLayer();
  useRotatedOverlay();

  const handleStartUpgrade = async (latitude: number, longitude: number) => {
    // Azure tiles are only added for a static -> geospatial upgrade with a chosen location
    if (!isStatic || upgradeInProg || latitude == null || longitude == null) return;

    try {
      // Static maps have no Azure layer from initialization, so build it now
      const layer = azureLayer ?? (await createAzureLayer(theme));
      if (!azureLayer) setAzureLayer(layer);

      startUpgrade(
        latitude,
        longitude,
        mapGeoJSON,
        mapInstance,
        boundsLayer,
        imageLayer,
        layer,
        setSelected,
        setUpgradeInProg,
        setOverlayFC,
      );
    } catch (err) {
      console.error('Geospatial upgrade failed:', err);
    }
  };

  return (
    <>
      <div
        id="myMap"
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '1em',
          boxShadow: 'var(--box-shadow-right)',
        }}
      />
      {!upgradeInProg && (
        <>
          {edit ? (
            <MapEditMenu />
          ) : (
            <>
              <MapInfoMenu />
              <MapTourStartMenu />
            </>
          )}
        </>
      )}
      <MapControls startUpgrade={handleStartUpgrade} />
    </>
  );
}
